import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogs } from '../services/api';

// TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const BlogSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [inView, setInView] = useState(false);
  const [visibleBlogs, setVisibleBlogs] = useState([]);
  const sectionRef = useRef(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getBlogs = async () => {
      try {
        const blogData = await fetchBlogs();
        // Limit to first 3 blogs
        setBlogs(blogData.slice(0, 3));
      } catch (err) {
        setError('Failed to load blogs');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getBlogs();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          // Animate blogs one by one with delays
          let delay = 0;
          const delays = [];
          blogs.forEach((_, i) => {
            delays.push(setTimeout(() => {
              setVisibleBlogs(prev => [...prev, i]);
            }, delay));
            delay += 200; // 200ms between each blog
          });

          return () => delays.forEach(t => clearTimeout(t));
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [blogs]);

  if (loading) {
    return <div className="text-white text-center py-12">Loading blogs...</div>;
  }

  if (error) {
    return <div className="text-white text-center py-12">{error}</div>;
  }

  return (
    <section ref={sectionRef} className="w-full text-white relative py-12 px-4 md:px-12">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .animate-fade-in {
            animation: fadeIn 0.8s ease-out forwards;
          }
          
          .animate-slide-up {
            animation: slideUp 0.6s ease-out forwards;
          }
        `}
      </style>

      <div className="container mx-auto px-4">
        <div className={`flex flex-col md:flex-row justify-between items-center mb-8 gap-4 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          <h2 className={`text-6xl font-bold lg:text-6xl ${inView ? 'animate-slide-up' : ''}`} style={{ animationDelay: '0ms' }}>
            {inView && (
              <TypewriterText
                text="Blogs"
                isActive={inView}
                delay={0}
              />
            )}
          </h2>
          {/* Desktop View More Button */}
          <button
            className={`hidden md:block bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors ${inView ? 'animate-slide-up' : ''}`}
            style={{ animationDelay: '500ms' }}
          >
            <Link to="/Blogs">View More</Link>
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-center gap-8">
          {blogs.map((blog, index) => {
            const isVisible = visibleBlogs.includes(index);
            const animationClass = isMobile ? 'animate-slide-up' : 'animate-fade-in';

            return (
              <div
                key={blog.id} // Use blog id
                className={`relative w-full md:w-1/4 lg:w-1/3 h-96 rounded-3xl overflow-hidden shadow-lg ${isVisible ? animationClass : 'opacity-0'
                  }`}
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${blog.thumbnail})`, filter: 'blur(5px)' }}
                />
                <div className="relative z-10 flex flex-col justify-between h-full p-8 bg-black bg-opacity-50 rounded-xl">
                  <div>
                    <div className='w-full h-full flex flex-col justify-between items-start mb-4'>
                      <h3 className="w-full text-xl lg:text-4xl font-semibold">{blog.title}</h3>
                      <p className="text-sm text-gray-300">{blog.date}</p>
                    </div>
                    {/* <p className="text-sm lg:text-lg mt-6">{blog.shortDescription}</p> */}
                  </div>
                  <Link
                    to={`/Blogs#blog-${blog.id}`}
                    className="group text-blue-300 hover:underline mt-4 inline-block"
                  >
                    <span className='text-white flex items-center'>
                      Learn More
                      <svg
                        className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 ml-1 transform transition-transform duration-300 group-hover:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        {/* Mobile View More Button at the bottom */}
        <div className={`block md:hidden text-center mt-8 ${inView ? 'animate-slide-up' : 'opacity-0'}`}
          style={{ animationDelay: `${blogs.length * 200 + 200}ms` }}>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors w-full max-w-xs mx-auto"
          >
            <Link to="/Blogs">View More</Link>
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;